import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import md5 from "md5";
import { Table } from "react-bootstrap";
import SwipeableViews from "react-swipeable-views";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import styled from "styled-components";
import socket from "../../../Socket";
import storage from "../../../Storage";
import UserModal from "../../Components/User/Stat/Modal";
import {
  __,
  fixDate,
  Event,
  wait,
  decode,
  encode,
  forceSatoshiFormat,
  timeConvertor,
  isMobile,
} from "../../../Helper";
import { userUID } from "../../../Auth";
import C from "../../../Constant";
import History from "./History";

// Helper function to truncate names to 5 characters with ellipsis
const truncateName = (name, maxLength = 5) => {
  if (!name || typeof name !== "string") return "";
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength) + "..";
};

// Styled Components
const MainContainer = styled.div`
  width: 100%;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const TabContainer = styled.div`
  overflow-x: hidden;
`;

const StyledAppBar = styled(AppBar)`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const StyledTab = styled(Tab)`
  min-height: 48px;
  font-size: 14px;

  @media (max-width: 600px) {
    font-size: 14px;
    min-width: auto;
    padding: 6px 8px;
  }
`;

const TabIcon = styled.i`
  margin-right: 8px;
  vertical-align: middle;
  font-size: 14px;

  @media (max-width: 600px) {
    font-size: 14px;
    margin-right: 4px;
  }
`;

const TableResponsive = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-bottom: 0;
`;

const StyledTable = styled(Table)`
  width: 100%;
  margin-bottom: 0;

  & th,
  & td {
    padding: 10px 5px;
    font-size: 10px;

    @media (max-width: 768px) {
      padding: 10px 10px;
      font-size: 14px;
    }

    @media (max-width: 576px) {
      padding: 8px 8px;
      font-size: 14px;
    }
  }

  & th {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 14px;
  }

  & td {
    // border-top: 1px solid rgba(255, 255, 255, 0.05);
    font-size: 14px;
  }
`;

const MiniCoin = styled.img`
  width: 18px;
  height: 18px;
  margin-right: 5px;
  vertical-align: middle;

  @media (max-width: 576px) {
    width: 14px;
    height: 14px;
    margin-right: 3px;
  }
`;

const AlertBox = styled.div`
  padding: 12px;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.2);
  text-align: center;
  margin: 16px 0;
  font-size: 14px;
`;

const UserRow = styled.tr`
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const TextSuccess = styled.span`
  color: #28a745;
`;

const TextDanger = styled.span`
  color: #dc3545;
`;

const TextWarning = styled.span`
  color: #ffc107;
`;

const HighlightRow = styled(UserRow)`
  background-color: rgba(255, 193, 7, 0.1);
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 20px 0;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;

  img {
    width: 120px;
    height: 120px;
    margin-bottom: 16px;
    opacity: 0.7;
  }

  p {
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    margin: 0;
  }
`;

const PlayerName = styled.td`
  display: flex;
  align-items: center;
  font-size: 10px;

  // & > *:first-child {
  //   margin-right: 8px;
  // }
`;

// Helper function to format numbers to 2 decimal places
const formatToTwoDecimals = (value) => {
  if (value === "-" || value === undefined || value === null) return "-";
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return value;
  return numValue.toFixed(2);
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={0}>
          <Typography component={"span"}>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

function MobileQueue(props) {
  const theme = useTheme();
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleChangeIndex = (index) => {
    setValue(index);
  };

  let { game, engine } = props;
  let isLogged = storage.getKey("logged");

  function load() {
    wait(500).then(() => {
      engine.getPlayers();
    });
  }

  return (
    <MainContainer>
      <StyledAppBar position="static" color="transparent">
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="primary"
          variant="fullWidth"
          aria-label="game stats tabs"
          centered
        >
          <StyledTab
            onClick={load}
            icon={<TabIcon className="mdi mdi-chart-bar" />}
            label="Stats"
            {...a11yProps(0)}
          />
          <StyledTab
            icon={<TabIcon className="mdi mdi-chart-areaspline" />}
            label="My Bets"
            {...a11yProps(1)}
          />
          <StyledTab
            icon={<TabIcon className="mdi mdi-chart-donut" />}
            label="History"
            {...a11yProps(2)}
          />
        </Tabs>
      </StyledAppBar>
      <SwipeableViews
        axis={"x"}
        index={value}
        onChangeIndex={handleChangeIndex}
        resistance
      >
        <TabPanel value={value} index={0} dir={"x"}>
          <AllBets engine={engine} t={props.t} game={game} />
        </TabPanel>
        <TabPanel value={value} index={1} dir={"x"}>
          {isLogged !== null && (
            <MyBets engine={engine} t={props.t} game={game} />
          )}
          {isLogged === null && (
            <AlertBox>
              <Typography variant="body2" className="text-white">
                You must be logged in to see your stats
              </Typography>
            </AlertBox>
          )}
        </TabPanel>
        <TabPanel value={value} index={2} dir={"x"}>
          <History tab={true} engine={engine} t={props.t} game={game} />
        </TabPanel>
      </SwipeableViews>
    </MainContainer>
  );
}

function AllBets({ engine, t, game }) {
  const [players, setPlayers] = useState([]);
  const [winners, setWinners] = useState([]);
  const [isWinner, setIsWinner] = useState(false);

  const handleChange = (players, winner) => {
    setPlayers(players);
    setIsWinner(winner);
  };

  const handleWinner = (winners) => {
    setWinners(winners);
  };

  return (
    <Child engine={engine} onChange={handleChange} onWinner={handleWinner} />
  );
}

class Child extends React.Component {
  _isMounted = false;
  constructor(props) {
    super(props);
    this.state = {
      playersObject: [],
      winnersObject: [],
    };
  }

  componentDidMount() {
    this._isMounted = true;
    if (this._isMounted) {
      let { engine } = this.props;
      engine.on("finish_crash", (data) => this.syncFinishPlayer(data));
      engine.on("play_crash", (data) => this.syncPlayPlayer(data));
      engine.on("busted_crash", (data) => this.busted(data));
      engine.on("waiting_crash", (data) => this.isWaiting(data));
      engine.on("game_status", (data) => this.gameSync(data));
      engine.on("game_players", (data) => this.gameSync(data));
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  syncPlayPlayer(data) {
    if (this._isMounted) {
      let add = this.state.playersObject.concat(data);
      this.setState({ playersObject: add });
      this.props.onChange(this.state.playersObject);
    }
  }

  syncFinishPlayer(data) {
    if (this._isMounted) {
      let index = __.findIndex(this.state.playersObject, function (o) {
        return o.uid === data.uid;
      });
      this.state.playersObject.splice(index, 1);
      let add = this.state.winnersObject.concat(data);
      this.setState({ winnersObject: add });
      this.props.onWinner(this.state.winnersObject);
    }
  }

  gameSync(data) {
    if (this._isMounted) {
      const playersArray = [...this.state.playersObject];
      sortByAmount(data.players).forEach((item) => {
        playersArray.push(item);
      });
      this.setState({ playersObject: playersArray });
      this.props.onChange(playersArray);

      const winnersArray = [...this.state.winnersObject];
      sortByCashout(data.winners).forEach((item) => {
        winnersArray.push(item);
      });
      this.setState({ winnersObject: winnersArray });
      this.props.onWinner(winnersArray, true);
    }
  }

  busted(data) {
    if (this._isMounted) {
      const playersArray = [];
      const winnersArray = [];

      sortByAmount(data.players).forEach((item) => {
        playersArray.push(item);
      });
      this.setState({ playersObject: playersArray });
      this.props.onChange(playersArray);

      sortByCashout(data.winners).forEach((item) => {
        winnersArray.push(item);
      });
      this.setState({ winnersObject: winnersArray });
      this.props.onWinner(winnersArray, true);
    }
  }

  isWaiting(data) {
    if (this._isMounted) {
      const playersArray = [];

      sortByAmount(data.players).forEach((item) => {
        playersArray.push(item);
      });
      this.setState({ playersObject: playersArray, winnersObject: [] });
      this.props.onChange(playersArray);
    }
  }

  render() {
    return (
      <ShowUserTable
        engine={this.props.engine}
        players={this.state.playersObject}
        winners={this.state.winnersObject}
      />
    );
  }
}

function UsersTable({ engine, players, winners }) {
  const [fontSize, setFontSize] = useState("");

  useEffect(() => {
    if (isMobile()) {
      setFontSize("font-10");
    }
  }, []);

  let playerRows = [];
  let winnerRows = [];
  let checkExists = [];

  if (players.length > 0) {
    players.forEach((currentPlayer, i) => {
      if (!checkExists.includes(currentPlayer.name)) {
        checkExists.push(currentPlayer.name);
        if (currentPlayer.session === "crash") {
          playerRows.push(
            <UserRowComponent
              engine={engine}
              currentPlayer={currentPlayer}
              key={i}
              isWinner={false}
              isFailed={false}
            />
          );
        }
      }
    });
  }

  if (winners.length > 0) {
    winners.forEach((currentPlayer, i) => {
      if (!checkExists.includes(currentPlayer.name)) {
        checkExists.push(currentPlayer.name);
        if (currentPlayer.session === "crash")
          winnerRows.push(
            <UserRowComponent
              engine={engine}
              currentPlayer={currentPlayer}
              key={i}
              isWinner={true}
              isFailed={false}
            />
          );
      }
    });
  }

  // Check if both arrays are empty
  const isEmpty = playerRows.length === 0 && winnerRows.length === 0;

  if (isEmpty) {
    return (
      <EmptyState>
        <img
          src="/assets/bcimages/bcaffiliate/reward-activity-empty.webp"
          alt="No data available"
        />
        <p>No betting activity found</p>
      </EmptyState>
    );
  }

  return (
    <TableResponsive>
      <StyledTable className={fontSize}>
        <thead>
          <tr style={{ fontSize: "12px" }}>
            <th className={"text-center"}>PLAYER</th>
            <th className={"text-center"}>PAYOUT</th>
            <th className={"text-center"}>BET</th>
            <th className={"text-center"}>PROFIT</th>
          </tr>
        </thead>
        <tbody>
          {playerRows}
          {winnerRows}
        </tbody>
      </StyledTable>
    </TableResponsive>
  );
}

function ShowUserTable({ engine, players, winners }) {
  var size = 50;
  var playersList = sortByAmount(players);
  var winnersList = sortByCashout(winners);
  playersList.length = Math.min(playersList.length, size);
  winnersList.length = Math.min(winnersList.length, size);

  return (
    <UsersTable engine={engine} players={playersList} winners={winnersList} />
  );
}

const StyledUserRow = styled(UserRow)`
  ${(props) =>
    props.self &&
    `
    background-color: rgba(255, 193, 7, 0.1);
  `}

  ${(props) =>
    props.type === "success" &&
    `
    color: #28a745;
  `}
  
  ${(props) =>
    props.type === "danger" &&
    `
    color: #dc3545;
  `}
  
  ${(props) =>
    props.type === "warning" &&
    `
    color: #ffc107;
  `}
`;

function UserRowComponent({ currentPlayer, engine, isWinner, isFailed }) {
  if (currentPlayer.uid === undefined || currentPlayer.name === undefined)
    return null;

  let profit = currentPlayer.won;
  let self = parseFloat(currentPlayer.uid) === parseFloat(userUID());
  let type = "warning";
  let cashout = "-";
  let bet = formatToTwoDecimals(currentPlayer.amount);

  if (engine.gameStatus === "busted") {
    if (profit === "-") type = "danger";
  }

  if (profit !== "-") {
    type = "success";
    cashout = (currentPlayer.current / 100).toFixed(2) + "x";
    profit = formatToTwoDecimals(profit);
  }

  return (
    <StyledUserRow
      key={currentPlayer.uid}
      self={self}
      type={type}
      className={`q-crash text-center`}
    >
      <PlayerName className="text-center">
        <UserModal
          username={truncateName(currentPlayer.name)}
          id={currentPlayer.uid}
          queue={true}
          avatar={null}
          isWinner={profit}
        />
      </PlayerName>
      <td className="text-center" size="20">
        {cashout}
      </td>
      <td className="text-center num-style">
        <MiniCoin src={`/assets/images/${currentPlayer.coin}.png`} alt="COIN" />
        {bet}{" "}
        <span className="d-none d-sm-inline">
          {__.upperCase(currentPlayer.coin)}
        </span>{" "}
      </td>
      <td className="text-center num-style">
        <MiniCoin src={`/assets/images/${currentPlayer.coin}.png`} alt="COIN" />
        {profit}{" "}
        <span className="d-none d-sm-inline">
          {__.upperCase(currentPlayer.coin)}
        </span>
      </td>
    </StyledUserRow>
  );
}

function MyBets({ engine, t, game }) {
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [name] = useState(storage.getKey("name"));
  const [gameInfo] = useState({
    game: "crash",
    gameCode: md5("crash"),
  });

  useEffect(() => {
    socket.emit(C.MY_BETS, encode({ game: gameInfo.game }));

    const myBetsHandler = (data) => {
      makeList(decode(data));
    };

    const addBetHandler = (data) => {
      addList(decode(data));
    };

    socket.on(C.MY_BETS, myBetsHandler);
    socket.on(C.ADD_BET, addBetHandler);

    return () => {
      socket.off(C.MY_BETS, myBetsHandler);
      socket.off(C.ADD_BET, addBetHandler);
    };
  }, [gameInfo.game]);

  const addList = (player) => {
    if (player.name === name) {
      setPlayers((prevPlayers) => [
        <Players forMy={true} player={player} />,
        ...prevPlayers,
      ]);
    }
  };

  const makeList = (arr) => {
    if (typeof arr.history === "undefined") return;
    setLoading(false);

    const newPlayers = [];
    arr.history.forEach((player, i) => {
      newPlayers.push(<Players key={i} forMy={true} player={player} />);
    });

    setPlayers(newPlayers);
  };

  // Limit displayed players to 10
  const displayedPlayers = players.slice(0, 10);

  if (loading) {
    return (
      <LoadingSpinner>
        <div className="spinner-grow text-white my-3" role="status" />
      </LoadingSpinner>
    );
  }

  // Show empty state if no players data
  if (displayedPlayers.length === 0) {
    return (
      <EmptyState>
        <img
          src="/assets/bcimages/bcaffiliate/reward-activity-empty.webp"
          alt="No bets found"
        />
        <p>No betting history found</p>
      </EmptyState>
    );
  }

  return (
    <TableResponsive>
      <StyledTable className="text-center mb-0">
        <thead>
          <tr style={{ fontSize: "12px" }}>
            <th>ID</th>
            <th>Amount</th>
            <th>Profit</th>
            {/* <th>Date/Time</th> */}
          </tr>
        </thead>
        <tbody>{displayedPlayers}</tbody>
      </StyledTable>
    </TableResponsive>
  );
}

const PlayerRow = styled(UserRow)`
  cursor: pointer;

  ${(props) =>
    props.isWinner &&
    `
    td.profit {
      color: #28a745;
    }
  `}
`;

function Players({ player, forMy }) {
  const gameDetail = () => {
    Event.emit("single_game_modal", { data: player });
  };

  const { uid, name, amount, profit, coin, created, gid, direct } = player;
  const isWinner = __.toNumber(profit) !== 0.0;
  const date = direct ? timeConvertor(created) : fixDate(created);

  // Format amount and profit to 2 decimal places
  const formattedAmount = formatToTwoDecimals(amount);
  const formattedProfit = formatToTwoDecimals(profit);

  return (
    <PlayerRow isWinner={isWinner} className="q-crash" onClick={gameDetail}>
      <td>{gid}</td>
      {forMy === false && (
        <PlayerName>
          <UserModal
            id={uid}
            username={truncateName(name)}
            isWinner={isWinner}
          />
        </PlayerName>
      )}
      <td className={`${isWinner ? "profit" : ""} num-style`}>
        <MiniCoin
          src={`/assets/images/${__.upperCase(coin)}.png`}
          className="d-none d-sm-inline"
          alt={coin}
        />
        {formattedAmount} {coin}
      </td>
      <td className={`${isWinner ? "profit" : ""} num-style`}>
        <MiniCoin
          src={`/assets/images/${__.upperCase(coin)}.png`}
          className="d-none d-sm-inline"
          alt={coin}
        />
        {formattedProfit} {coin}
      </td>
      {/* <td>{date}</td> */}
    </PlayerRow>
  );
}

function sortByAmount(input) {
  function r(c) {
    return c.amount ? -c.amount : null;
  }
  return __.sortBy(input, r);
}

function sortByCashout(input) {
  function r(c) {
    return c.current ? -c.current : null;
  }
  return __.sortBy(input, r);
}

export default MobileQueue;
